/**
 * Main menu functionality for responsive behaviors and dropdowns.
 *
 * @package  Embark
 * @since    1.0.0
 */

/**
 * Import dependencies.
 */
import hoverintent from 'hoverintent';
import {
	disableScroll, enableScroll, isElementInViewport, slideToggle,
} from './helpers';

const { els, breakpoints } = embark;
const {
	html,
	mainMenu,
	navicon,
	navContainer,
} = els;

/**
 * Check if the site should be displaying the mobile menu or not.
 */
export function isMobileMenu() {
	return breakpoints.menu.belowOrTouch();
}

/**
 * Check if the site should be displaying the desktop menu or not.
 */
export function isDesktopMenu() {
	return !isMobileMenu();
}

export class ResponsiveMenu {
	hoverIntentTimeout = 200;

	constructor({
		menu, navicon, container, menuOpenNaviconClass, menuOpenHtmlClass, menuItemTriggerClass,
	}) {
		if (menu && navicon && container && menuOpenNaviconClass && menuOpenHtmlClass && menuItemTriggerClass) {
			// The menu element.
			this.menu = menu;
			// The button that open's and closes the menu on mobile/touch devices.
			this.navicon = navicon;
			// The element that hidden/shown when the navicon is clicked.
			this.container = container;
			// THe class to add to the navicon element when the mobile menu is open.
			this.menuOpenNaviconClass = menuOpenNaviconClass;
			// The class to add to the html element when the mobile menu is open.
			this.menuOpenHtmlClass = menuOpenHtmlClass;
			// The class of the element that, when clicked, opens its sibling .sub-menu element.
			this.menuItemTriggerClass = menuItemTriggerClass;

			this.init();
		}
	}

	/**
	 * Set some initial menu item classes and all events.
	 */
	init = () => {
		// Add event listeners to menu items that have children.
		this.parentItems = this.menu.querySelectorAll('.menu-item-has-children');
		if (this.parentItems.length) {
			this.parentItems.forEach((item) => {
				hoverintent(item, () => {
					item.classList.add('hover');
				}, () => {
					item.classList.remove('hover');
				}).options({
					timeout: this.hoverIntentTimeout,
				});

				item.addEventListener('mouseenter', this.handleOffscreenSubmenus);
				item.addEventListener('click', this.handleTriggerClick);
				this.handleOffscreenSubmenus(item);

				// Expand current menu items on mobile.
				if (item.matches('[class*="current"]') && isMobileMenu()) {
					const submenu = item.querySelector('.sub-menu');
					item.classList.add('open');
					submenu.style.display = 'block';
				}
			});
		}

		// Add class to the first .current-menu-ancestor to avoid multiple being highlighted.
		this.ancestorItems = document.querySelectorAll('.main-menu > .current-menu-ancestor');
		if (this.ancestorItems.length > 0) {
			this.ancestorItems[0].classList.add('current-menu-ancestor--primary');
		}

		window.addEventListener('resize', this.handleResize);
		document.addEventListener('click', this.handleMenuState);
	}

	/**
	 * Check if current submenu is fully within the viewport.
	 * If not, add a class so that it can be styled accordingly.
	 *
	 * Note: 'this' refers to the menu item the event is added to,
	 * not the MainMenu class instance.
	 */
	handleOffscreenSubmenus(item = null) {
		if (isDesktopMenu()) {
			const menuItem = item || this;
			if (menuItem) {
				const submenu = menuItem.querySelector('.sub-menu');
				if (submenu && !isElementInViewport(submenu)) {
					menuItem.classList.add('has-offscreen-sub-menu');
				}
			}
		}
	}

	/**
	 * Toggle mobile submenus when a menu item link with a hash or the
	 * menu item trigger is clicked.
	 *
	 * Note: 'this' refers to the menu item the event is added to,
	 * not the MainMenu class instance.
	 *
	 * @param  {Event} e
	 */
	handleTriggerClick = (e) => {
		// Prevents further propagation of the current event in the capturing and bubbling phases.
		e.stopPropagation();

		// Get the submenu based on what was clicked.
		let submenu;
		const { target } = e;
		if (target.href) {
			// Handle links that have # hashes. These should open the child submenu when clicked.
			const hash = target.href.substring(target.href.lastIndexOf('/') + 1);
			if (hash && hash === '#') {
				submenu = target.nextElementSibling.nextElementSibling;
			}
		} else if (target.classList.contains(this.menuItemTriggerClass)) {
			submenu = target.nextElementSibling;
		}

		// If we have a submenu, expand it.
		if (submenu) {
			e.preventDefault();

			const menuItem = submenu.closest('.menu-item');
			const menuItemLink = menuItem.querySelector('a'); // Get the first link only, which should be the direct child link.
			const ariaExpanded = menuItemLink.getAttribute('aria-expanded');
			menuItemLink.setAttribute('aria-expanded', ariaExpanded === 'true' ? 'false' : 'true');
			menuItem.classList.toggle('open');

			if (isMobileMenu()) {
				slideToggle(submenu);
			}
		}
	}

	/**
	 * Update mobile menu top offset and close opened submenus depending on screensize.
	 */
	handleResize = () => {
		window.requestAnimationFrame(() => {
			if (isDesktopMenu()) {
				this.closeMobileMenu();
				this.closeOpenSubmenus();
			}
		});
	}

	/**
	 * Open the mobile menu.
	 */
	openMobileMenu = () => {
		disableScroll();
		html.classList.add(this.menuOpenHtmlClass);
		this.navicon.classList.add(this.menuOpenNaviconClass);
	}

	/**
	 * Close the mobile menu.
	 */
	closeMobileMenu = () => {
		// Make sure there isn't a lightbox open before we enable scrolling.
		if (!html.classList.contains('lightbox-open')) {
			enableScroll();
		}

		html.classList.remove(this.menuOpenHtmlClass);
		this.navicon.classList.remove(this.menuOpenNaviconClass);
	}

	/**
	 * Toggle the mobile menu.
	 */
	toggleMobileMenu = () => {
		if (html.classList.contains(this.menuOpenHtmlClass)) {
			this.closeMobileMenu();
		} else {
			this.openMobileMenu();
		}
	}

	/**
	 * Close the mobile menu when you click off of it.
	 *
	 * @param  {Event} e
	 */
	handleMenuState = (e) => {
		const { target } = e;
		const isTargetInNavigation = target.closest('.site-header__navigation');

		// If the menu is open, and the event target is the nav or a child of the nav, exit quickly.
		if (html.classList.contains(this.menuOpenHtmlClass) && (target === this.container || isTargetInNavigation)) {
			return;
		}

		// If event target is the navicon or any of its children, trigger the toggle method.
		if (target === this.navicon || target.closest('#js-navicon')) {
			this.toggleMobileMenu();
		} else {
			// Else, this is not the navicon trigger. Close the menu. User has clicked away.
			this.closeMobileMenu();
		}
	}

	/**
	 * Close all open submenus.
	 */
	closeOpenSubmenus = () => {
		const openMenuItems = this.menu.querySelectorAll('.menu-item.open');
		if (openMenuItems) {
			openMenuItems.forEach((menuItem) => {
				menuItem.classList.remove('open');
				const submenus = menuItem.querySelectorAll('.sub-menu');
				submenus.forEach((submenu) => {
					submenu.style.display = '';
				});
			});
		}
	}
}

// eslint-disable-next-line no-unused-vars
const mainMenuInstance = new ResponsiveMenu({
	menu: mainMenu,
	navicon,
	container: navContainer,
	menuOpenNaviconClass: 'navicon--open',
	menuOpenHtmlClass: 'html--menu-open',
	menuItemTriggerClass: 'menu-item__trigger',
});
